import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { Sunrise } from "lucide-react";
import { signUpSchema } from "@/lib/validations/auth";

export default function JobSeekerSignup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [consents, setConsents] = useState({
    terms: false,
    privacy: false,
    criminalRecord: false,
    dataSharing: false,
  });
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
  });

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required consents
    if (!consents.terms || !consents.privacy || !consents.criminalRecord || !consents.dataSharing) {
      toast.error("Please accept all required consents to continue");
      return;
    }

    setLoading(true);

    try {
      // Validate form data
      const validatedData = signUpSchema.parse({
        ...formData,
        role: "candidate"
      });

      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email: validatedData.email,
        password: validatedData.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: validatedData.full_name,
          },
        },
      });

      if (error) throw error;

      if (data.user) {
        // Record consents
        const consentRecords: Database["public"]["Tables"]["consent_records"]["Insert"][] = [
          {
            user_id: data.user.id,
            purpose: "essential",
            consent_given: true,
            consent_method: "signup_checkbox",
            consent_text: "I accept the Terms of Service",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          },
          {
            user_id: data.user.id,
            purpose: "essential",
            consent_given: true,
            consent_method: "signup_checkbox",
            consent_text: "I have read and understood the Privacy Policy",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          },
          {
            user_id: data.user.id,
            purpose: "profiling",
            consent_given: true,
            consent_method: "signup_checkbox",
            consent_text: "I consent to the processing of my criminal record data for employment matching",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          },
          {
            user_id: data.user.id,
            purpose: "third_party_sharing",
            consent_given: true,
            consent_method: "signup_checkbox",
            consent_text: "I consent to sharing my profile with potential employers",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          },
        ];

        await supabase.from("consent_records").insert(consentRecords);

        // Insert user role
        await supabase.from("user_roles").insert({
          user_id: data.user.id,
          role: "candidate",
          approval_status: "approved",
        });

        toast.success("Welcome to RePath! Let's get started on your journey.");
        navigate("/onboarding");
      }
    } catch (error: any) {
      if (error.errors) {
        // Zod validation errors
        error.errors.forEach((err: any) => {
          toast.error(err.message);
        });
      } else {
        toast.error(error.message || "Sign up failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--jobseeker-bg))] flex flex-col">
      {/* Header */}
      <header className="p-6">
        <div className="flex items-center gap-2">
          <Sunrise className="w-8 h-8 text-[hsl(var(--jobseeker-blue))]" />
          <h1 className="text-2xl font-bold text-foreground">RePath</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <div className="relative w-full h-48 mb-6 rounded-lg overflow-hidden bg-gradient-to-r from-[hsl(var(--jobseeker-blue))] to-[hsl(var(--jobseeker-gold))] flex items-center justify-center">
              <div className="absolute inset-0 opacity-20">
                <svg viewBox="0 0 400 200" className="w-full h-full">
                  <path 
                    d="M0,100 Q100,50 200,100 T400,100" 
                    fill="none" 
                    stroke="white" 
                    strokeWidth="3"
                    opacity="0.5"
                  />
                  <circle cx="350" cy="40" r="30" fill="white" opacity="0.3" />
                </svg>
              </div>
              <div className="relative z-10 text-white">
                <Sunrise className="w-16 h-16 mx-auto mb-2" />
                <p className="text-sm font-medium">A New Beginning</p>
              </div>
            </div>
            
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Start Fresh
            </h2>
            <p className="text-muted-foreground">
              Create your account and connect with employers who see your potential.
            </p>
          </div>

          {/* Signup Form */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <form onSubmit={handleSignUp} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  placeholder="Your full name"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="your@email.com"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Create a strong password"
                  className="border-border"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Min 8 characters, include uppercase, lowercase, and number
                </p>
              </div>

              {/* Consents */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={consents.terms}
                    onCheckedChange={(checked) =>
                      setConsents({ ...consents, terms: checked as boolean })
                    }
                    required
                  />
                  <Label htmlFor="terms" className="text-sm cursor-pointer leading-relaxed">
                    I agree to the{" "}
                    <a href="/terms" className="text-[hsl(var(--jobseeker-blue))] hover:underline">
                      Terms of Service
                    </a>
                  </Label>
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="privacy"
                    checked={consents.privacy}
                    onCheckedChange={(checked) =>
                      setConsents({ ...consents, privacy: checked as boolean })
                    }
                    required
                  />
                  <Label htmlFor="privacy" className="text-sm cursor-pointer leading-relaxed">
                    I agree to the{" "}
                    <a href="/privacy" className="text-[hsl(var(--jobseeker-blue))] hover:underline">
                      Privacy Policy
                    </a>
                  </Label>
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="criminal"
                    checked={consents.criminalRecord}
                    onCheckedChange={(checked) =>
                      setConsents({ ...consents, criminalRecord: checked as boolean })
                    }
                    required
                  />
                  <Label htmlFor="criminal" className="text-sm cursor-pointer leading-relaxed">
                    I consent to sharing my criminal record with potential employers
                  </Label>
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="sharing"
                    checked={consents.dataSharing}
                    onCheckedChange={(checked) =>
                      setConsents({ ...consents, dataSharing: checked as boolean })
                    }
                    required
                  />
                  <Label htmlFor="sharing" className="text-sm cursor-pointer leading-relaxed">
                    I consent to sharing my profile data with employers and service providers
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[hsl(var(--jobseeker-gold))] text-foreground hover:bg-[hsl(var(--jobseeker-gold))]/90 font-semibold py-6 text-lg shadow-md"
              >
                {loading ? "Creating account..." : "Start Your Journey"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{" "}
                <a href="/jobseeker-auth" className="text-[hsl(var(--jobseeker-blue))] hover:underline font-semibold">
                  Log In
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="p-6 border-t border-border/50">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <a href="/privacy" className="hover:text-foreground transition-colors">
            Privacy Policy
          </a>
          <a href="/terms" className="hover:text-foreground transition-colors">
            Terms of Service
          </a>
          <a href="mailto:admin@sccapp.co.uk" className="hover:text-foreground transition-colors">
            Contact Support
          </a>
        </div>
      </footer>
    </div>
  );
}
